import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import { 
  FileText, 
  Upload, 
  Table, 
  Download,
  CheckCircle,
  Zap,
  Shield,
  ChevronRight,
  BarChart3,
  Clock,
  Layers,
  Lock,
  ExternalLink,
  Trash2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const features = [
  {
    icon: Table,
    title: "Smart Table Detection",
    description: "Automatically extract Structural Framing Schedules with cut length sorting."
  },
  {
    icon: Layers,
    title: "Batch Management",
    description: "Organize supports into batches with tracking for cut completion and missing parts."
  },
  {
    icon: BarChart3,
    title: "Progress Tracking",
    description: "Track fabrication progress with auto-saving checkboxes and date fields."
  },
  {
    icon: Download,
    title: "Excel Export",
    description: "Export all supports to a single Excel file with complete tracking data."
  }
];

const HomePage = () => {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [processingStage, setProcessingStage] = useState("");
  // Merge-prompt state. When the upload response indicates the new PDF
  // shares an S-number with an existing extraction, we surface a modal
  // before navigating away.
  const [mergePrompt, setMergePrompt] = useState(null); // { newId, newPfs, candidates }
  const [mergePrimary, setMergePrimary] = useState("");
  const [mergeBusy, setMergeBusy] = useState(false);

  const handleFileUpload = useCallback(async (file) => {
    // Validate file type
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error("Invalid file type", {
        description: "Please upload a PDF file."
      });
      return;
    }

    // Validate file size (100MB)
    if (file.size > 100 * 1024 * 1024) {
      toast.error("File too large", {
        description: "Maximum file size is 100 MB."
      });
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);
    setProcessingStage("Checking for duplicates...");

    const formData = new FormData();
    formData.append("file", file);

    try {
      // Simulate upload progress
      const progressInterval = setInterval(() => {
        setUploadProgress((prev) => {
          if (prev >= 30) {
            clearInterval(progressInterval);
            return 30;
          }
          return prev + 5;
        });
      }, 100);

      setProcessingStage("Uploading...");
      
      const response = await axios.post(`${API}/upload`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 30) / progressEvent.total
          );
          setUploadProgress(percentCompleted);
        },
      });

      clearInterval(progressInterval);
      setUploadProgress(50);
      setProcessingStage("Processing PDF...");
      
      // Simulate processing stages
      await new Promise(resolve => setTimeout(resolve, 300));
      setUploadProgress(70);
      setProcessingStage("Extracting supports...");
      
      await new Promise(resolve => setTimeout(resolve, 300));
      setUploadProgress(90);
      setProcessingStage("Finalizing...");
      
      await new Promise(resolve => setTimeout(resolve, 200));
      setUploadProgress(100);
      setProcessingStage("Complete!");

      toast.success("PDF processed successfully!", {
        description: `Extracted ${response.data.table_count || 0} supports from ${response.data.page_count} pages`
      });

      // Merge prompt: if the backend detected a shared S-number with an
      // existing extraction, surface the modal *before* navigating.
      if (response.data.merge_required) {
        setIsUploading(false);
        setUploadProgress(0);
        setProcessingStage("");
        const cands = response.data.merge_candidates || [];
        const newPfs = response.data.new_drawing_numbers || [];
        setMergePrompt({
          newId: response.data.id,
          newPfs,
          candidates: cands,
        });
        // Default primary = the new file's first PF#
        setMergePrimary(newPfs[0] || (cands[0]?.existing_drawing_numbers?.[0] || ""));
        return;
      }

      // Navigate to results
      setTimeout(() => {
        navigate(`/results/${response.data.id}`);
      }, 500);

    } catch (error) {
      console.error("Upload error:", error);
      const errorMessage = error.response?.data?.detail || "Failed to process PDF. Please try again.";
      
      // Check if it's a duplicate error
      if (error.response?.status === 409) {
        toast.error("Duplicate PDF detected", {
          description: errorMessage
        });
      } else {
        toast.error("Upload failed", {
          description: errorMessage
        });
      }
      setIsUploading(false);
      setUploadProgress(0);
      setProcessingStage("");
    }
  }, [navigate]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileUpload(files[0]);
    }
  }, [handleFileUpload]);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      handleFileUpload(file);
    }
  };

  // Merge prompt handlers
  const confirmMerge = async () => {
    if (!mergePrompt) return;
    const target = mergePrompt.candidates[0];
    if (!target) return;
    setMergeBusy(true);
    try {
      const resp = await axios.post(`${API}/extraction/${mergePrompt.newId}/merge`, {
        target_id: target.extraction_id,
        primary_drawing_number: mergePrimary,
      });
      toast.success("PDFs merged", {
        description: `${mergePrimary || "Primary drawing"} now has ${resp.data.page_count} pages`,
      });
      const mergedId = resp.data.id;
      setMergePrompt(null);
      setMergeBusy(false);
      navigate(`/results/${mergedId}`);
    } catch (err) {
      console.error("Merge failed:", err);
      toast.error(err.response?.data?.detail || "Failed to merge PDFs");
      setMergeBusy(false);
    }
  };

  const dismissMerge = async () => {
    if (!mergePrompt) return;
    const id = mergePrompt.newId;
    setMergeBusy(true);
    try {
      await axios.post(`${API}/extraction/${id}/dismiss-merge`);
    } catch (err) {
      // Non-fatal: the flag is only advisory.
      console.warn("dismiss-merge failed:", err);
    }
    setMergePrompt(null);
    setMergeBusy(false);
    navigate(`/results/${id}`);
  };

  // Delete the *existing* conflicting extraction (the older upload the new PDF
  // clashed with) — useful when the admin realises it's an orphan sitting in
  // the DB with no batch and no longer needed. After the delete, we dismiss
  // the prompt on the new upload and navigate to the fresh results page.
  //
  // The backend refuses to delete an extraction that has dependent batches
  // unless `?force=true` is passed (returns 409). We forward that choice via
  // an explicit second confirm so batches are never removed accidentally.
  const deleteConflicting = async (candidate) => {
    if (!mergePrompt || !candidate) return;
    const batchCount = candidate.batch_count ?? 0;
    const label = candidate.filename || candidate.extraction_id;

    if (batchCount === 0) {
      if (!window.confirm(
        `Delete existing extraction "${label}"?\n\nIt has no batches — safe to delete.`
      )) return;
    } else {
      if (!window.confirm(
        `"${label}" is referenced by ${batchCount} batch(es).\n\n` +
        `Deleting it will ALSO delete those batches (cascade). ` +
        `Their fabrication progress will be lost.\n\n` +
        `Continue?`
      )) return;
    }

    const newId = mergePrompt.newId;
    setMergeBusy(true);
    try {
      await axios.delete(
        `${API}/extraction/${candidate.extraction_id}` +
        (batchCount > 0 ? "?force=true" : "")
      );
      toast.success(
        batchCount > 0
          ? `Deleted "${label}" and ${batchCount} dependent batch(es)`
          : `Deleted existing extraction "${label}"`
      );
      // Dismiss the merge flag on the new upload and open it.
      try { await axios.post(`${API}/extraction/${newId}/dismiss-merge`); } catch { /* advisory */ }
      setMergePrompt(null);
      navigate(`/results/${newId}`);
    } catch (err) {
      toast.error(err.response?.data?.detail?.message || err.response?.data?.detail || "Failed to delete existing extraction");
    } finally {
      setMergeBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <main className="flex-1">
        <section className="relative bg-gradient-to-br from-blue-50 via-white to-blue-50 py-16 lg:py-24">
          {/* Background pattern */}
          <div className="absolute inset-0 opacity-30">
            <div className="absolute top-20 left-10 w-72 h-72 bg-blue-200 rounded-full blur-3xl"></div>
            <div className="absolute bottom-20 right-10 w-96 h-96 bg-blue-100 rounded-full blur-3xl"></div>
          </div>
          
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              {/* Left: Content */}
              <div className="animate-fade-in">
                <div className="inline-flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-full text-sm font-medium mb-6 shadow-lg">
                  <Zap className="w-4 h-4" />
                  Structural Framing Made Simple
                </div>
                
                <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
                  Support<span className="text-blue-600">IQ</span>
                </h1>
                
                <p className="text-lg text-gray-600 mb-8 leading-relaxed max-w-lg">
                  Extract, organize, and track your Structural Framing Schedules. 
                  Upload PDF fab packs and manage supports with intelligent batch tracking.
                </p>

                <div className="flex flex-wrap gap-4 mb-8">
                  <div className="flex items-center gap-2 text-gray-600">
                    <CheckCircle className="w-5 h-5 text-blue-600" />
                    <span className="text-sm">Auto-sorted by Cut Length</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-600">
                    <CheckCircle className="w-5 h-5 text-blue-600" />
                    <span className="text-sm">Cut Complete Tracking</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-600">
                    <CheckCircle className="w-5 h-5 text-blue-600" />
                    <span className="text-sm">Missing Part Detection</span>
                  </div>
                </div>

                <div className="flex flex-wrap gap-3">
                  <Button 
                    size="lg" 
                    className="bg-blue-600 hover:bg-blue-700 text-white shadow-lg shadow-blue-200 px-8"
                    onClick={() => isAdmin && document.getElementById('file-input').click()}
                    disabled={!isAdmin}
                    data-testid="upload-pdf-btn"
                  >
                    {isAdmin ? (
                      <>
                        <Upload className="w-5 h-5 mr-2" />
                        Upload PDF
                      </>
                    ) : (
                      <>
                        <Lock className="w-5 h-5 mr-2" />
                        Admin Only
                      </>
                    )}
                  </Button>
                  <Button 
                    size="lg" 
                    variant="outline"
                    className="border-blue-200 text-blue-700 hover:bg-blue-50"
                    onClick={() => navigate('/projects')}
                    data-testid="view-batches-btn"
                  >
                    View Saved Batches
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              </div>

              {/* Right: Upload Zone */}
              <div className="animate-fade-in delay-100">
                {isAdmin ? (
                  <div 
                    className={`relative rounded-2xl border-2 border-dashed p-12 text-center transition-all duration-300 bg-white shadow-xl ${
                      isDragging 
                        ? 'border-blue-500 bg-blue-50 scale-[1.02]' 
                        : 'border-blue-200 hover:border-blue-400 hover:shadow-2xl'
                    }`}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    data-testid="upload-zone"
                  >
                    {isUploading ? (
                      <div className="space-y-6">
                        <div className="w-20 h-20 mx-auto bg-blue-100 rounded-full flex items-center justify-center">
                          <FileText className="w-10 h-10 text-blue-600 animate-pulse" />
                        </div>
                        <div>
                          <p className="text-lg font-semibold text-gray-900 mb-2">
                            {processingStage}
                          </p>
                          <Progress value={uploadProgress} className="h-2 max-w-xs mx-auto" />
                          <p className="text-sm text-gray-500 mt-2">{uploadProgress}%</p>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="w-20 h-20 mx-auto bg-blue-100 rounded-full flex items-center justify-center mb-6">
                          <Upload className="w-10 h-10 text-blue-600" />
                        </div>
                        <h3 className="text-xl font-semibold text-gray-900 mb-2">
                          Drop your PDF here
                        </h3>
                        <p className="text-gray-500 mb-6">
                          or click to browse files
                        </p>
                        <input
                          id="file-input"
                          type="file"
                          accept=".pdf"
                          className="hidden"
                          onChange={handleFileSelect}
                          data-testid="file-input"
                        />
                        <label
                          htmlFor="file-input"
                          className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg font-medium cursor-pointer hover:bg-blue-700 transition-colors shadow-lg"
                        >
                          <FileText className="w-5 h-5" />
                          Select PDF File
                        </label>
                        <p className="text-xs text-gray-400 mt-4">
                          Supports PDF up to 100MB
                        </p>
                      </>
                    )}
                  </div>
                ) : (
                  /* Non-admin view - Batches quick access */
                  <div className="relative rounded-2xl border-2 border-gray-200 p-12 text-center bg-white shadow-xl">
                    <div className="w-20 h-20 mx-auto bg-gray-100 rounded-full flex items-center justify-center mb-6">
                      <Lock className="w-10 h-10 text-gray-400" />
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">
                      Upload Restricted
                    </h3>
                    <p className="text-gray-500 mb-6">
                      Only administrators can upload new PDFs.
                      <br />
                      You can view and edit existing batches.
                    </p>
                    <Button 
                      onClick={() => navigate('/projects')}
                      className="bg-blue-600 hover:bg-blue-700"
                      data-testid="go-to-batches-btn"
                    >
                      <Table className="w-5 h-5 mr-2" />
                      View Saved Batches
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section id="features" className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16 animate-fade-in">
              <h2 className="font-heading text-3xl font-bold text-gray-900 mb-4">
                Streamline Your Workflow
              </h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                Support IQ extracts and organizes your structural framing data automatically, 
                so you can focus on fabrication, not paperwork.
              </p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {features.map((feature, index) => (
                <div 
                  key={index}
                  className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 hover:shadow-lg hover:border-blue-100 transition-all duration-300 animate-fade-in"
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                    <feature.icon className="w-6 h-6 text-blue-600" />
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-2">{feature.title}</h3>
                  <p className="text-sm text-gray-500">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section id="how-it-works" className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="font-heading text-3xl font-bold text-gray-900 mb-4">
                How It Works
              </h2>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {[
                { step: "1", title: "Upload PDF", desc: "Drop your fab pack PDF and we'll extract all Structural Framing Schedules" },
                { step: "2", title: "Create Batch", desc: "Organize supports into batches with auto-detected project info" },
                { step: "3", title: "Track & Export", desc: "Mark cuts complete, track missing parts, and export to Excel" }
              ].map((item, idx) => (
                <div key={idx} className="text-center">
                  <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4 text-white text-2xl font-bold shadow-lg">
                    {item.step}
                  </div>
                  <h3 className="font-semibold text-gray-900 text-lg mb-2">{item.title}</h3>
                  <p className="text-gray-500">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />

      {/* Merge prompt modal */}
      {mergePrompt && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="merge-modal">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full p-6">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
                <Layers className="w-5 h-5 text-amber-700" />
              </div>
              <div>
                <h3 className="font-heading font-bold text-lg text-gray-900">Shared S-number detected</h3>
                <p className="text-sm text-gray-600 mt-1">
                  The uploaded PDF (<span className="font-mono">{(mergePrompt.newPfs || []).join(", ") || "new file"}</span>) shares engineering drawing number
                  <span className="font-mono ml-1">{(mergePrompt.candidates?.[0]?.shared_eng_drawing_numbers || []).join(", ")}</span>{" "}
                  with an existing extraction
                  <span className="font-mono ml-1">({(mergePrompt.candidates?.[0]?.existing_drawing_numbers || []).join(", ")})</span>.
                </p>
                {mergePrompt.candidates?.[0] && (
                  <div className="mt-3 p-3 rounded-md bg-gray-50 border border-gray-200 text-xs space-y-1" data-testid="merge-candidate-info">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-gray-500">Existing file:</span>
                      <span className="font-mono text-gray-800 truncate max-w-[280px]" title={mergePrompt.candidates[0].filename}>
                        {mergePrompt.candidates[0].filename || "(no filename)"}
                      </span>
                    </div>
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-gray-500">Uploaded:</span>
                      <span className="text-gray-700">
                        {mergePrompt.candidates[0].created_at
                          ? new Date(mergePrompt.candidates[0].created_at).toLocaleString()
                          : "unknown"}
                      </span>
                    </div>
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-gray-500">Pages · Batches using it:</span>
                      <span className="text-gray-700">
                        {mergePrompt.candidates[0].page_count || "?"} page(s) ·{" "}
                        <span className={mergePrompt.candidates[0].batch_count ? "text-emerald-700 font-semibold" : "text-amber-700 font-semibold"}>
                          {mergePrompt.candidates[0].batch_count ?? 0} batch(es)
                        </span>
                      </span>
                    </div>
                    <div className="flex items-center gap-2 pt-2 mt-2 border-t border-gray-200">
                      <a
                        href={`/results/${mergePrompt.candidates[0].extraction_id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 font-medium"
                        data-testid="merge-view-existing-link"
                      >
                        <ExternalLink className="w-3 h-3" /> View existing
                      </a>
                      <button
                        type="button"
                        onClick={() => deleteConflicting(mergePrompt.candidates[0])}
                        disabled={mergeBusy}
                        className="inline-flex items-center gap-1 text-xs text-red-600 hover:text-red-800 font-medium disabled:opacity-50"
                        data-testid="merge-delete-existing-btn"
                      >
                        <Trash2 className="w-3 h-3" /> Delete existing
                      </button>
                    </div>
                  </div>
                )}
                <p className="text-sm text-gray-600 mt-3">
                  Would you like to merge them into one combined PDF? The fabricator&apos;s &quot;View PDF&quot; button will then open both pages.
                </p>
                <p className="text-xs text-gray-500 mt-2 italic">
                  If these are unrelated drawings that happen to reference the same S-number, click <span className="font-medium">Keep separate</span>.
                </p>
              </div>
            </div>

            <div className="space-y-3 mb-5">
              <label className="block text-sm font-medium text-gray-700">
                Choose the primary drawing number
              </label>
              <select
                value={mergePrimary}
                onChange={(e) => setMergePrimary(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={mergeBusy}
                data-testid="merge-primary-select"
              >
                {[...(mergePrompt.newPfs || []),
                  ...(mergePrompt.candidates?.[0]?.existing_drawing_numbers || [])]
                  .filter((v, i, a) => v && a.indexOf(v) === i)
                  .map(pf => (
                    <option key={pf} value={pf}>{pf}</option>
                  ))}
              </select>
              <p className="text-xs text-gray-500">
                The other drawing number(s) will be kept as a secondary reference.
              </p>
            </div>

            <div className="flex items-center justify-end gap-2">
              <Button
                variant="outline"
                onClick={dismissMerge}
                disabled={mergeBusy}
                data-testid="merge-dismiss-btn"
              >
                Keep separate
              </Button>
              <Button
                onClick={confirmMerge}
                disabled={mergeBusy || !mergePrimary}
                className="bg-blue-600 hover:bg-blue-700 text-white"
                data-testid="merge-confirm-btn"
              >
                {mergeBusy ? "Merging..." : "Merge PDFs"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HomePage;
